/// <amd-module name="TFS/Admin/Views/Dashboards/DashboardsPermissions" />
import React = require("react");
import Resources = require("TFS/Admin/Views/Dashboards/Resources");

import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { SubjectPermission, SubjectPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { AccessControlEntry, AceExtendedInformation } from "TFS/Admin/Common/Contracts/Security";
import { ISecurityRestClient, SecurityClientName } from "TFS/Admin/Common/RestClient/Security";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { ITfsPageService } from "Tfs/Platform/Page";
import { ObservableValue } from "VSS/Core/Observable";
import { IdentityDescriptor } from "VSS/Platform/Contracts/Identities";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Card } from "VSSUI/Card";
import { CustomHeader, HeaderDescription, HeaderTitle, HeaderTitleArea, HeaderTitleRow, TitleSize } from "VSSUI/Header";
import { Icon } from "VSSUI/Icon";
import { Page } from "VSSUI/Page";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Toggle } from "VSSUI/Toggle";
import { Location } from "VSSUI/Utilities/Position";

interface IDashboardsComponentState {
    teamPermissions: SubjectPermissions;
}

const createDashboardsCheckbox = new ObservableValue<boolean>(false);
const editDashboardsCheckbox = new ObservableValue<boolean>(false);
const deleteDashboardsCheckbox = new ObservableValue<boolean>(false);
const DashboardsPermissionsDataProvider = "ms.vss-admin-web.org-admin-dashboards-permissions-data-provider";
const AdminTeamsDataProvider = "ms.vss-admin-web.admin-teams-data-provider";
const TeamData = "ms.vss-tfs-web.team-data";
const DashboardsPermissionSetId = "8adf73b7-389a-4276-b638-fe1653f7efc7";

export class DashboardsComponent extends VssComponent<{}, IDashboardsComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);

    public constructor(props: {}, context?: any) {
        super(props, context);

        this.state = {
            teamPermissions: {
                canEditPermissions: false
            } as SubjectPermissions
        };
    }

    public componentDidMount() {
        // Fetch selected team and the dashboard permissions for the team
        this._fetchInitialData();
    }

    public render(): JSX.Element {
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <CustomHeader>
                        <HeaderTitleArea>
                            <HeaderTitleRow>
                                <HeaderTitle className="text-ellipsis" titleSize={TitleSize.Large}>
                                    {Resources.DashboardsPageTitle}
                                </HeaderTitle>
                            </HeaderTitleRow>
                            <HeaderDescription aria-label={Resources.DashboardsHeaderDescription}>
                                {Resources.DashboardsHeaderDescription}
                            </HeaderDescription>
                        </HeaderTitleArea>
                    </CustomHeader>

                    <div className="page-content page-content-top">
                        <Card>
                            <div className="rhythm-vertical-24 flex-column">
                                <div className="rhythm-vertical-4 flex-column">
                                    <div className="flex-row flex-grow">
                                        <Toggle
                                            className="icon-margin"
                                            offText={Resources.CreateDashboards}
                                            onText={Resources.CreateDashboards}
                                            checked={createDashboardsCheckbox}
                                            onChange={(event, value) => {
                                                createDashboardsCheckbox.value = value;
                                                this._setPermission(
                                                    value,
                                                    this.state.teamPermissions.identityDescriptor,
                                                    this.state.teamPermissions.subjectPermissions[0]
                                                );
                                            }}
                                            disabled={!this.state.teamPermissions.canEditPermissions}
                                            ariaLabel={Resources.CreateDashboards}
                                        />
                                        <Icon
                                            iconName="Info"
                                            ariaLabel={Resources.CreateDashboardsDescription}
                                            tooltipProps={{
                                                anchorOrigin: { horizontal: Location.end, vertical: Location.center },
                                                text: Resources.CreateDashboardsDescription,
                                                tooltipOrigin: { horizontal: Location.start, vertical: Location.center }
                                            }}
                                            tabIndex={0}
                                        />
                                    </div>
                                </div>
                                <div className="rhythm-vertical-4 flex-column">
                                    <div className="flex-row flex-grow">
                                        <Toggle
                                            className="icon-margin"
                                            offText={Resources.EditDashboards}
                                            onText={Resources.EditDashboards}
                                            checked={editDashboardsCheckbox}
                                            onChange={(event, value) => {
                                                editDashboardsCheckbox.value = value;
                                                this._setPermission(
                                                    value,
                                                    this.state.teamPermissions.identityDescriptor,
                                                    this.state.teamPermissions.subjectPermissions[1]
                                                );
                                            }}
                                            disabled={!this.state.teamPermissions.canEditPermissions}
                                            ariaLabel={Resources.EditDashboards}
                                        />
                                        <Icon
                                            iconName="Info"
                                            ariaLabel={Resources.EditDashboardsDescription}
                                            tooltipProps={{
                                                anchorOrigin: { horizontal: Location.end, vertical: Location.center },
                                                text: Resources.EditDashboardsDescription,
                                                tooltipOrigin: { horizontal: Location.start, vertical: Location.center }
                                            }}
                                            tabIndex={0}
                                        />
                                    </div>
                                </div>
                                <div className="rhythm-vertical-4 flex-column">
                                    <div className="flex-row flex-grow">
                                        <Toggle
                                            className="icon-margin"
                                            offText={Resources.DeleteDashboards}
                                            onText={Resources.DeleteDashboards}
                                            checked={deleteDashboardsCheckbox}
                                            onChange={(event, value) => {
                                                deleteDashboardsCheckbox.value = value;
                                                this._setPermission(
                                                    value,
                                                    this.state.teamPermissions.identityDescriptor,
                                                    this.state.teamPermissions.subjectPermissions[2]
                                                );
                                            }}
                                            disabled={!this.state.teamPermissions.canEditPermissions}
                                            ariaLabel={Resources.DeleteDashboards}
                                        />
                                        <Icon
                                            iconName="Info"
                                            ariaLabel={Resources.DeleteDashboardsDescription}
                                            tooltipProps={{
                                                anchorOrigin: { horizontal: Location.end, vertical: Location.center },
                                                text: Resources.DeleteDashboardsDescription,
                                                tooltipOrigin: { horizontal: Location.start, vertical: Location.center }
                                            }}
                                            tabIndex={0}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </Page>
            </Surface>
        );
    }

    private async _fetchInitialData() {
        var telemetry = this._startActionCapture("FetchTeamsFromDataProvider", {});
        try {
            const contributionService = await this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            // Fetch selected team accross project
            const teamData = await contributionService.getData<any>(TeamData);

            const selectedTeam = await contributionService.getDataAsync<any>(AdminTeamsDataProvider, {
                getTeam: true,
                teamId: teamData.team.id
            });

            // Fetch team permissions
            if (selectedTeam) {
                const vssPageService = await this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
                const pageData = await vssPageService.getData();

                if (pageData && pageData.project) {
                    const teamPermissionsData = await contributionService.getDataAsync<SubjectPermissions>(DashboardsPermissionsDataProvider, {
                        subjectDescriptor: selectedTeam.identity.subjectDescriptor,
                        permissionSetId: DashboardsPermissionSetId,
                        permissionSetToken: '{"teamId":"' + selectedTeam.identity.id + '"}'
                    });
                    if (teamPermissionsData) {
                        teamPermissionsData.subjectPermissions = teamPermissionsData.subjectPermissions.sort((a, b) => a.bit - b.bit);
                        createDashboardsCheckbox.value = teamPermissionsData.subjectPermissions[0].effectivePermissionValue == 1 ? true : false;
                        editDashboardsCheckbox.value = teamPermissionsData.subjectPermissions[1].effectivePermissionValue == 1 ? true : false;
                        deleteDashboardsCheckbox.value = teamPermissionsData.subjectPermissions[2].effectivePermissionValue == 1 ? true : false;
                        this.setState({ teamPermissions: teamPermissionsData });
                    } else {
                        telemetry.captureError("Error fetching dashboards permissions");
                    }
                }
            }
            const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            performanceService.markTimeToInteractive(Area.Admin, Feature.ProjectDashboards);
        } catch (error) {
            telemetry.captureError(error);
        }
    }

    private async _setPermission(checked: boolean, identityDescriptor: IdentityDescriptor, subjectPermission: SubjectPermission) {
        const { bit, token, namespaceId } = subjectPermission;
        var telemetry = this._startActionCapture("setDashboardPermissionAtTeamLevel", {});
        const securityClient: ISecurityRestClient = await this.context.pageContext.getRestClient<ISecurityRestClient>(SecurityClientName);

        var allow: number = 0;
        var deny: number = 0;

        if (checked) {
            allow = bit;
        } else {
            deny = bit;
        }

        const aceExtendedInfo: AceExtendedInformation = {
            effectiveAllow: allow,
            effectiveDeny: deny,
            inheritedAllow: allow,
            inheritedDeny: deny
        };

        const setAccessControlEntriesItem: AccessControlEntry = {
            descriptor: identityDescriptor,
            allow: allow,
            deny: deny,
            extendedInfo: aceExtendedInfo
        };

        const container = {
            token: token,
            merge: true,
            accessControlEntries: [setAccessControlEntriesItem]
        };

        try {
            await securityClient.setAccessControlEntries(container, namespaceId);
        } catch (error) {
            telemetry.captureError(error);
        }
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.ProjectDashboards, Feature.ProjectDashboards, action, properties);
    };
}

Components.add("ms.vss-admin-web.dashboards-component", DashboardsComponent);
